import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LanguageToggle } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API helper — resilient to the platform monitoring script (emergent-main.js)
// which wraps window.fetch and can consume error-response bodies, otherwise
// causing a misleading "body stream already read" message instead of the real error.
const api = {
  post: async (endpoint, data) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      let payload = null;
      try { payload = await res.clone().json(); } catch (e) { /* body maybe consumed */ }
      if (payload === null) { try { payload = await res.json(); } catch (e) { /* ignore */ } }
      if (payload === null) {
        try { const t = await res.text(); payload = t ? JSON.parse(t) : null; } catch (e) { /* ignore */ }
      }
      if (payload === null) {
        // Body unreadable → give a clean, status-based message.
        if (res.ok) return { success: true };
        if (res.status === 401) return { error: 'गलत phone या password / Invalid phone or password' };
        if (res.status === 422) return { error: 'कृपया phone और password सही भरें' };
        if (res.status === 429) return { error: 'बहुत ज़्यादा कोशिशें — थोड़ी देर बाद try करें' };
        return { error: `Server error (HTTP ${res.status})` };
      }
      return payload;
    } catch (err) {
      console.error('API error:', err);
      return { error: 'Connection error — internet/नेटवर्क जाँचें' };
    }
  }
};

const getApiErrorMessage = (payload, fallback = 'Login failed') => {
  if (!payload) return fallback;
  if (typeof payload.detail === 'string') return payload.detail;
  if (typeof payload.error === 'string') return payload.error;
  if (typeof payload.message === 'string') return payload.message;
  if (Array.isArray(payload.detail)) {
    return payload.detail.map((item) => item?.msg || JSON.stringify(item)).join(' ');
  }
  return fallback;
};

const getAuthPayload = (payload) => ({
  token: payload?.access_token || payload?.token || null,
  user: payload?.user || null,
});

export function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Forgot-password flow
  const [fpOtpSent, setFpOtpSent] = useState(false);
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [showFpPassword, setShowFpPassword] = useState(false);
  const [info, setInfo] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/auth/login', { phone, password });
      const { token, user } = getAuthPayload(res);
      if (token && user) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        onLogin(user, token);
      } else {
        setError(getApiErrorMessage(res, 'Login failed'));
      }
    } catch (err) {
      setError('Connection error');
    }
    setLoading(false);
  };

  const handleOtpLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!otpSent) {
      const res = await api.post('/api/auth/send-otp', { phone });
      if (res.success) {
        setOtpSent(true);
        setError('WhatsApp पर OTP भेज दिया गया है');
      } else {
        setError(getApiErrorMessage(res, 'Failed to send OTP'));
      }
    } else {
      const res = await api.post('/api/auth/verify-otp', { phone, otp });
      const { token, user } = getAuthPayload(res);
      if (token && user) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        onLogin(user, token);
      } else {
        setError(getApiErrorMessage(res, 'Invalid OTP'));
      }
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');
    if (!fpOtpSent) {
      const res = await api.post('/api/auth/send-otp', { phone });
      if (res.success) {
        setFpOtpSent(true);
        setInfo('WhatsApp पर OTP भेज दिया गया है — OTP और नया password भरें।');
      } else {
        setError(getApiErrorMessage(res, 'OTP भेजने में समस्या — दोबारा प्रयास करें'));
      }
    } else {
      const res = await api.post('/api/auth/reset-password', { phone, otp, new_password: fpNewPassword });
      if (res.success) {
        setInfo('✅ Password बदल गया! अब नए password से login करें।');
        setLoginMode('password');
        setFpOtpSent(false);
        setOtp('');
        setFpNewPassword('');
        setPassword('');
      } else {
        setError(getApiErrorMessage(res, 'Password reset नहीं हो पाया'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 flex flex-col">
      {/* Language Toggle for Login Page */}
      <div className="absolute top-4 right-4">
        <LanguageToggle className="bg-white/80 shadow-sm" />
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          {/* Back to Home Button */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition"
            data-testid="login-back-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>वापस जाएं / Go Back</span>
          </Link>
          
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-orange-600">शिक्षा मित्र</h1>
            <p className="text-gray-500 mt-2">School Management Platform</p>
            <p className="text-gray-400 text-sm mt-1">by System Of Success</p>
          </div>
          
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button onClick={() => { setLoginMode('password'); setOtpSent(false); setError(''); setInfo(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${loginMode === 'password' ? 'bg-white shadow' : ''}`} data-testid="tab-password">
              Password
            </button>
            <button onClick={() => { setLoginMode('otp'); setError(''); setInfo(''); }}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${loginMode === 'otp' ? 'bg-white shadow' : ''}`} data-testid="tab-otp">
              OTP Login
            </button>
          </div>

          {loginMode === 'password' && (
            <form onSubmit={handleLogin} className="space-y-6">
              {info && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg" data-testid="login-info">{info}</div>}
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="9876543210" required data-testid="login-phone-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    placeholder="••••••••" required data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="toggle-password-visibility">
                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <div className="text-right mt-2">
                  <button type="button" onClick={() => { setLoginMode('forgot'); setError(''); setInfo(''); setFpOtpSent(false); setOtp(''); setFpNewPassword(''); }}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium" data-testid="forgot-password-link">
                    Password भूल गए?
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
                data-testid="login-submit-btn">
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}

          {loginMode === 'otp' && (
            <form onSubmit={handleOtpLogin} className="space-y-6">
              {error && <div className={`px-4 py-3 rounded-lg ${error.includes('भेज दिया') ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="9876543210" required disabled={otpSent} data-testid="otp-phone-input" />
              </div>
              {otpSent && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enter OTP</label>
                  <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-center text-2xl tracking-widest"
                    placeholder="000000" maxLength="6" required data-testid="otp-input" />
                </div>
              )}
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-600 disabled:opacity-50"
                data-testid="otp-submit-btn">
                {loading ? 'Please wait...' : otpSent ? 'Verify OTP' : 'Send OTP'}
              </button>
              {otpSent && (
                <button type="button" onClick={() => { setOtpSent(false); setOtp(''); setError(''); }}
                  className="w-full text-sm text-gray-500 hover:text-gray-700">
                  Change Phone Number
                </button>
              )}
            </form>
          )}

          {loginMode === 'forgot' && (
            <form onSubmit={handleForgot} className="space-y-6" data-testid="forgot-password-form">
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-sm text-orange-700">
                🔑 Password reset — अपने registered phone पर WhatsApp OTP मँगाएँ, फिर नया password सेट करें।
              </div>
              {info && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">{info}</div>}
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="9876543210" required disabled={fpOtpSent} data-testid="fp-phone-input" />
              </div>
              {fpOtpSent && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp OTP</label>
                    <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-center text-2xl tracking-widest"
                      placeholder="000000" maxLength="6" required data-testid="fp-otp-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">नया Password</label>
                    <div className="relative">
                      <input type={showFpPassword ? 'text' : 'password'} value={fpNewPassword} onChange={(e) => setFpNewPassword(e.target.value)}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        placeholder="कम से कम 8 अक्षर" required data-testid="fp-new-password-input" />
                      <button type="button" onClick={() => setShowFpPassword(s => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label={showFpPassword ? 'Hide password' : 'Show password'} data-testid="toggle-fp-password-visibility">
                        <i className={`fa-solid ${showFpPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">बड़े अक्षर, छोटे अक्षर और अंक ज़रूरी हैं।</p>
                  </div>
                </>
              )}
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
                data-testid="fp-submit-btn">
                {loading ? 'Please wait...' : fpOtpSent ? 'Password Reset करें' : 'OTP भेजें'}
              </button>
              <button type="button" onClick={() => { setLoginMode('password'); setError(''); setInfo(''); setFpOtpSent(false); setOtp(''); setFpNewPassword(''); }}
                className="w-full text-sm text-gray-500 hover:text-gray-700" data-testid="fp-back-to-login">
                ← Login पर वापस जाएँ
              </button>
            </form>
          )}
          
          <div className="mt-6 p-4 bg-orange-50 rounded-lg text-sm text-gray-600 text-center">
            <p className="font-medium text-orange-700">शिक्षा मित्र — School Management Platform</p>
            <p className="text-gray-400 text-xs mt-1">Powered by System Of Success</p>
          </div>
        </div>
      </div>
      
      {/* Footer with Legal Links on Login Page */}
      <footer className="bg-gray-800 text-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-white transition">Privacy Policy</Link>
            <span className="text-gray-600">|</span>
            <Link to="/terms" className="text-gray-400 hover:text-white transition">Terms & Conditions</Link>
            <span className="text-gray-600">|</span>
            <Link to="/refund" className="text-gray-400 hover:text-white transition">Refund Policy</Link>
            <span className="text-gray-600">|</span>
            <Link to="/contact" className="text-gray-400 hover:text-white transition">Contact Us</Link>
            <span className="text-gray-600">|</span>
            <Link to="/about" className="text-gray-400 hover:text-white transition">About Us</Link>
          </div>
          <p className="text-center text-gray-500 text-xs mt-3">© 2025 System Of Success. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default LoginPage;
